/**
 * Local price alerts (owner 2026-09-27, mockup 02 "Alerts" module: Log / Rules / Watchers).
 * Rules are evaluated **client-side** against the live md prints, in integer ticks, print by
 * print, so a fast move through a level is never skipped. Nothing here trades: an alert is a toast
 * and a sound. Pure functions; the engine that feeds prints lives in apps/web.
 */
import { MINUS, formatTicks, type PriceFormat } from "./format.ts";

export type AlertCondition = "crossesAbove" | "crossesBelow" | "crosses";

export const ALERT_CONDITIONS: readonly { id: AlertCondition; label: string }[] = [
  { id: "crossesAbove", label: "Crosses above" },
  { id: "crossesBelow", label: "Crosses below" },
  { id: "crosses", label: "Crosses" },
];

export type AlertRule = {
  id: string;
  /** Requested symbol as typed / watched ("NQ", "ESZ6"). */
  symbol: string;
  condition: AlertCondition;
  /** Level in absolute ticks of the instrument. */
  priceTicks: number;
  enabled: boolean;
  /** Disarm after the first trigger (default) or keep firing on every cross. */
  once: boolean;
  note: string;
  createdMs: number;
  /** Times fired. */
  fired: number;
};

export type AlertEvent = {
  id: string;
  ruleId: string;
  symbol: string;
  condition: AlertCondition;
  priceTicks: number;
  /** Price before and at the triggering print, ticks. */
  fromTicks: number;
  toTicks: number;
  tsMs: number;
  note: string;
  /**
   * Chart alerts (drawings / indicators, `@helm/core/alerts`): the rule as text ("NQ 5m · Trend
   * line · Crossing up"), shown instead of the price-cross description. `priceTicks` is then the
   * level (rounded) or the price at the trigger.
   */
  text?: string;
};

/**
 * Crossing test for one price move `prev → next` (ticks). Touching the level counts: "above" fires
 * when prev < level ≤ next, "below" when prev > level ≥ next. A price that starts on the level
 * has not crossed it.
 */
export function crosses(cond: AlertCondition, level: number, prev: number, next: number): boolean {
  const up = prev < level && next >= level;
  const down = prev > level && next <= level;
  if (cond === "crossesAbove") return up;
  if (cond === "crossesBelow") return down;
  return up || down;
}

/**
 * Feed one symbol's prints (arrival order) through the rules. `prev` is the last price seen before
 * these prints (null = first data: nothing can have crossed yet). Returns the events and the
 * updated rules (fired counts; `once` rules disarm), plus the new last price.
 */
export function evaluatePrints(
  rules: readonly AlertRule[],
  symbol: string,
  prev: number | null,
  prints: readonly { priceTicks: number; tsMs: number }[],
  newId: () => string,
): { events: AlertEvent[]; rules: AlertRule[]; last: number | null } {
  let last = prev;
  const out: AlertEvent[] = [];
  const next = rules.map((r) => ({ ...r }));
  for (const p of prints) {
    if (last !== null && p.priceTicks !== last) {
      for (const r of next) {
        if (!r.enabled || r.symbol !== symbol) continue;
        if (!crosses(r.condition, r.priceTicks, last, p.priceTicks)) continue;
        r.fired += 1;
        if (r.once) r.enabled = false;
        out.push({
          id: newId(),
          ruleId: r.id,
          symbol,
          condition: r.condition,
          priceTicks: r.priceTicks,
          fromTicks: last,
          toTicks: p.priceTicks,
          tsMs: p.tsMs,
          note: r.note,
        });
      }
    }
    last = p.priceTicks;
  }
  return { events: out, rules: out.length > 0 ? next : [...rules], last };
}

/**
 * Parse a typed price into exact ticks for a tick size `num/den` ("21,450.25" → 85801 for 1/4).
 * Decimal string math, no float: the price must sit on the tick grid.
 */
export function parsePriceTicks(
  input: string,
  tickNum: number,
  tickDen: number,
): { ok: true; ticks: number } | { ok: false; message: string } {
  const s = input.trim().replace(/,/g, "");
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) return { ok: false, message: "Enter a positive price" };
  if (!(tickNum > 0) || !(tickDen > 0)) return { ok: false, message: "Tick size unknown yet" };
  const frac = m[2] ?? "";
  // price = digits / 10^k; ticks = price / (num/den) = digits·den / (10^k·num)
  const digits = BigInt((m[1] ?? "0") + frac);
  const scale = 10n ** BigInt(frac.length);
  const num = digits * BigInt(tickDen);
  const den = scale * BigInt(tickNum);
  if (num % den !== 0n) {
    return { ok: false, message: `Not on the tick grid (${String(tickNum)}/${String(tickDen)})` };
  }
  const ticks = num / den;
  if (ticks <= 0n || ticks > BigInt(Number.MAX_SAFE_INTEGER)) return { ok: false, message: "Out of range" };
  return { ok: true, ticks: Number(ticks) };
}

/** "NQ crosses above 21,450.25". */
export function describeRule(
  r: Pick<AlertRule, "symbol" | "condition" | "priceTicks">,
  fmt: PriceFormat | null,
): string {
  const label = ALERT_CONDITIONS.find((c) => c.id === r.condition)?.label.toLowerCase() ?? r.condition;
  return `${r.symbol} ${label} ${formatTicks(r.priceTicks, fmt)}`;
}

/** Arrow for an event (never colour alone, SPEC §17.4). */
export function eventArrow(e: Pick<AlertEvent, "fromTicks" | "toTicks">): "▲" | "▼" {
  return e.toTicks >= e.fromTicks ? "▲" : "▼";
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * Alert log as CSV (Export button): ISO time, symbol, condition (a chart alert's rule text), level,
 * from, to, note.
 */
export function alertLogCsv(
  events: readonly AlertEvent[],
  fmtOf: (symbol: string) => PriceFormat | null,
): string {
  const head = "time,symbol,condition,level,from,to,note";
  const rows = events.map((e) => {
    const f = fmtOf(e.symbol);
    const px = (t: number): string => formatTicks(t, f).replace(MINUS, "-");
    return [
      new Date(e.tsMs).toISOString(),
      e.symbol,
      e.text ?? e.condition,
      px(e.priceTicks),
      px(e.fromTicks),
      px(e.toTicks),
      e.note,
    ]
      .map(csvCell)
      .join(",");
  });
  return [head, ...rows].join("\n") + "\n";
}

/** Keep the stored list sane (local storage can hold anything). */
export function sanitizeRules(v: unknown): AlertRule[] {
  if (!Array.isArray(v)) return [];
  const out: AlertRule[] = [];
  for (const x of v as unknown[]) {
    if (typeof x !== "object" || x === null) continue;
    const r = x as Record<string, unknown>;
    const cond = r["condition"];
    if (
      typeof r["id"] !== "string" ||
      typeof r["symbol"] !== "string" ||
      !Number.isSafeInteger(r["priceTicks"]) ||
      (cond !== "crossesAbove" && cond !== "crossesBelow" && cond !== "crosses")
    )
      continue;
    out.push({
      id: r["id"],
      symbol: r["symbol"],
      condition: cond,
      priceTicks: r["priceTicks"] as number,
      enabled: r["enabled"] !== false,
      once: r["once"] !== false,
      note: typeof r["note"] === "string" ? r["note"] : "",
      createdMs: typeof r["createdMs"] === "number" ? r["createdMs"] : 0,
      fired: typeof r["fired"] === "number" ? r["fired"] : 0,
    });
  }
  return out;
}
